import { useEffect, useRef, useState } from 'react';
import './InstagramEmbed.css';

const EMBED_SCRIPT_URL = 'https://www.instagram.com/embed.js';

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

let scriptPromise: Promise<void> | null = null;

const loadEmbedScript = () => {
  if (window.instgrm) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = EMBED_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Instagram embed script failed to load'));
      };
      document.body.appendChild(script);
    });
  }
  return scriptPromise;
};

const toPermalink = (url: string) => {
  const match = url.match(/instagram\.com\/(p|reel)\/([^/?#]+)/);
  return match ? `https://www.instagram.com/${match[1]}/${match[2]}/` : url;
};

interface InstagramEmbedProps {
  url: string;
}

function InstagramEmbed({ url }: InstagramEmbedProps) {
  const permalink = toPermalink(url);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  // Meta's script and iframes are heavy, so wait until the post is about to scroll into view.
  useEffect(() => {
    const element = containerRef.current;
    if (!element || isNearViewport) return;

    if (!('IntersectionObserver' in window)) {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [isNearViewport]);

  // embed.js only scans the page once on load, so after client-side navigation we ask it to process again.
  useEffect(() => {
    if (!isNearViewport) return;

    loadEmbedScript()
      .then(() => window.instgrm?.Embeds.process())
      .catch(() => {
        // Blocked by an ad blocker or offline: the fallback link inside the blockquote stays visible.
      });
  }, [permalink, isNearViewport]);

  return (
    <div className="instagram-embed" ref={containerRef}>
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={`${permalink}?utm_source=ig_embed`}
        data-instgrm-version="14"
      >
        <a href={permalink} target="_blank" rel="noopener noreferrer">
          Переглянути пост в Instagram
        </a>
      </blockquote>
    </div>
  );
}

export default InstagramEmbed;
